import React, { useState } from 'react';
import { User, Mail, Phone, Building2, Users, Calendar, PhoneCall } from 'lucide-react';
import { submitAgencyApplication } from '../api/agency_applications';

// Agencies no longer pick a slot; an owner calls them directly. These notes
// keep the (NOT NULL) interview_date/interview_time columns and the admin
// notification email populated without changing any backend behavior.
const OWNER_CALL_DATE = 'Owner will call applicant';
const OWNER_CALL_TIME = 'their earliest availability';

interface FormData {
  name: string;
  email: string;
  phone: string;
  agencyName: string;
  yearsInBusiness: string;
  agencySize: string;
  callbackDate: string;
  callbackTime: string;
}

interface FormErrors {
  [key: string]: string;
}

const MeetUs: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    agencyName: '',
    yearsInBusiness: '',
    agencySize: '',
    callbackDate: OWNER_CALL_DATE,
    callbackTime: OWNER_CALL_TIME
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const agencySizeOptions = [
    { value: '1-5', label: '1-5 agents' },
    { value: '6-10', label: '6-10 agents' },
    { value: '11-25', label: '11-25 agents' },
    { value: '26-50', label: '26-50 agents' },
    { value: '51-100', label: '51-100 agents' },
    { value: '100+', label: '100+ agents' }
  ];

  const yearsOptions = [
    { value: 'less-than-1', label: 'Less than 1 year' },
    { value: '1-2', label: '1-2 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '6-10', label: '6-10 years' },
    { value: '11+', label: '11+ years' }
  ];

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      const [, area, exchange, number] = match;
      if (number) return `(${area}) ${exchange}-${number}`;
      if (exchange) return `(${area}) ${exchange}`;
      if (area) return `(${area}`;
    }
    return value;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    if (!formData.agencyName.trim()) newErrors.agencyName = 'Required';
    if (!formData.yearsInBusiness) newErrors.yearsInBusiness = 'Required';
    if (!formData.agencySize) newErrors.agencySize = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await submitAgencyApplication({
        fullName: formData.name,
        email: formData.email,
        phone: formData.phone,
        agencyName: formData.agencyName,
        agencySize: formData.agencySize,
        yearsInBusiness: formData.yearsInBusiness,
        interviewDate: OWNER_CALL_DATE,
        interviewTime: OWNER_CALL_TIME,
        source: 'agency_application_page'
      });

      if (!result.success) throw new Error(result.error || 'Failed');

      await fetch('https://hook.us2.make.com/o25onvy86rs6jwp6j1db12se2m4ke2nj', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'agency_application_page', timestamp: new Date().toISOString() })
      }).catch(() => {});

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    if (field === 'phone') value = formatPhone(value);
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white pt-28 pb-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Request submitted.</h2>
          <p className="text-slate-600 mb-6">
            One of our owners will give you a call shortly. Talk soon.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
          >
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-10">
            <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-3">
              For agencies.
            </h1>
            <p className="text-base sm:text-lg text-slate-600">
              You've built something. Now you want to scale your agency and maximize your earnings. Let's talk about how we can help.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Contact Info */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">Your Info</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Your Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="John Smith"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="john@agency.com"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      maxLength={14}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Agency Name *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="text"
                      value={formData.agencyName}
                      onChange={(e) => handleChange('agencyName', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.agencyName ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="Your Agency LLC"
                    />
                  </div>
                  {errors.agencyName && <p className="text-red-500 text-xs mt-1">{errors.agencyName}</p>}
                </div>
              </div>
            </div>

            {/* Agency Details */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">Agency Details</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Years in Business *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <select
                      value={formData.yearsInBusiness}
                      onChange={(e) => handleChange('yearsInBusiness', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all appearance-none text-slate-900 ${errors.yearsInBusiness ? 'border-red-400' : 'border-slate-200'}`}
                    >
                      <option value="" className="text-slate-500">Select</option>
                      {yearsOptions.map(opt => <option key={opt.value} value={opt.value} className="text-slate-900">{opt.label}</option>)}
                    </select>
                  </div>
                  {errors.yearsInBusiness && <p className="text-red-500 text-xs mt-1">{errors.yearsInBusiness}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Agency Size *</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <select
                      value={formData.agencySize}
                      onChange={(e) => handleChange('agencySize', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all appearance-none text-slate-900 ${errors.agencySize ? 'border-red-400' : 'border-slate-200'}`}
                    >
                      <option value="" className="text-slate-500">Select</option>
                      {agencySizeOptions.map(opt => <option key={opt.value} value={opt.value} className="text-slate-900">{opt.label}</option>)}
                    </select>
                  </div>
                  {errors.agencySize && <p className="text-red-500 text-xs mt-1">{errors.agencySize}</p>}
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div className="flex items-start gap-3 rounded-lg border border-navy-100 bg-navy-50 p-4 sm:p-5">
              <PhoneCall className="w-5 h-5 text-navy-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-sm sm:text-base font-semibold text-slate-900">What happens next</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Once you submit, one of our owners will personally give you a call. No scheduling needed, just keep an eye on your phone.
                </p>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 sm:py-4 bg-navy-600 text-white font-semibold text-sm sm:text-base rounded-lg hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Request Consultation'}
            </button>
          </form>

          {/* Info Section */}
          <div className="mt-8 sm:mt-12 p-4 sm:p-6 bg-slate-50 rounded-xl">
            <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">What we talk about:</h3>
            <ul className="space-y-2 text-slate-600 text-sm">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-navy-600 rounded-full mt-1.5 flex-shrink-0"></span>
                <span>How to access top carrier partnerships</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-navy-600 rounded-full mt-1.5 flex-shrink-0"></span>
                <span>Building your own lead store for your agents</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-navy-600 rounded-full mt-1.5 flex-shrink-0"></span>
                <span>Setting up dialers and CRM systems</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-navy-600 rounded-full mt-1.5 flex-shrink-0"></span>
                <span>Whatever's on your mind, we'll answer</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetUs;
